import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useRecorder — multi-mode recorder for Jixxeo AI.
 *   mode === 'screen'  → getDisplayMedia (+ optional mic) — desktop & some tablets only
 *   mode === 'camera'  → getUserMedia({video, audio}) — works on mobile browsers too
 *   mode === 'audio'   → getUserMedia({audio}) — universal fallback
 * Video modes composite frames through a canvas that bakes a "Jixxeo AI" watermark
 * into every exported frame. Audio mode records straight from the mic.
 */
export function useRecorder({ watermarkText = 'Jixxeo AI', watermarkPosition = 'bottom-right',
                              watermarkOpacity = 0.7, quality = 'standard',
                              maxDurationSec = 1800, onError } = {}) {
  const [state, setState] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [pauseSupported, setPauseSupported] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [tabAudioActive, setTabAudioActive] = useState(false);
  const [activeMode, setActiveMode] = useState(null);

  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioDestRef = useRef(null);
  const videoElRef = useRef(null);
  const rafRef = useRef(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const accumRef = useRef(0);
  const timerRef = useRef(0);
  const maxTimerRef = useRef(0);
  const stopResolveRef = useRef(null);

  // Feature detection
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
  const hasMediaRecorder = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined';
  const capabilities = {
    screen: !!(md && typeof md.getDisplayMedia === 'function' && hasMediaRecorder),
    camera: !!(md && typeof md.getUserMedia === 'function' && hasMediaRecorder),
    audio:  !!(md && typeof md.getUserMedia === 'function' && hasMediaRecorder),
  };
  const anySupported = capabilities.screen || capabilities.camera || capabilities.audio;

  const cleanup = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    clearTimeout(maxTimerRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch {}
    }
    recorderRef.current = null;
    [displayStreamRef, micStreamRef, cameraStreamRef, canvasStreamRef].forEach(r => {
      if (r.current) { try { r.current.getTracks().forEach(t => t.stop()); } catch {} r.current = null; }
    });
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    videoElRef.current = null; audioDestRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const drawWatermark = useCallback((ctx, w, h) => {
    if (!watermarkText) return;
    const fontSize = Math.max(14, Math.round(Math.min(w, h) * 0.028));
    ctx.save();
    ctx.font = `700 ${fontSize}px "Outfit", "Plus Jakarta Sans", system-ui, sans-serif`;
    ctx.textBaseline = 'middle';
    const pad = fontSize * 0.9;
    const textW = ctx.measureText(watermarkText).width;
    const boxW = textW + pad * 1.4;
    const boxH = fontSize * 2;
    let x, y;
    switch (watermarkPosition) {
      case 'bottom-left':  x = pad * 0.5; y = h - boxH - pad * 0.5; break;
      case 'top-left':     x = pad * 0.5; y = pad * 0.5; break;
      case 'top-right':    x = w - boxW - pad * 0.5; y = pad * 0.5; break;
      default:             x = w - boxW - pad * 0.5; y = h - boxH - pad * 0.5;
    }
    ctx.globalAlpha = watermarkOpacity * 0.55;
    ctx.fillStyle = '#0A0A12';
    const r = boxH / 2;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + boxW, y, x + boxW, y + boxH, r);
    ctx.arcTo(x + boxW, y + boxH, x, y + boxH, r);
    ctx.arcTo(x, y + boxH, x, y, r);
    ctx.arcTo(x, y, x + boxW, y, r);
    ctx.closePath();
    ctx.fill();
    const grad = ctx.createLinearGradient(x, y, x + boxW, y + boxH);
    grad.addColorStop(0, '#A78BFA');
    grad.addColorStop(1, '#22D3EE');
    ctx.globalAlpha = watermarkOpacity;
    ctx.fillStyle = grad;
    ctx.fillText(watermarkText, x + pad * 0.7, y + boxH / 2);
    ctx.globalAlpha = watermarkOpacity;
    ctx.fillStyle = '#F59E0B';
    ctx.beginPath();
    ctx.arc(x + boxW - pad * 0.35, y + boxH / 2, fontSize * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }, [watermarkText, watermarkPosition, watermarkOpacity]);

  const pickVideoMime = () => {
    const cs = [
      'video/mp4;codecs=avc1,mp4a',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    return cs.find(m => window.MediaRecorder.isTypeSupported(m)) || 'video/webm';
  };
  const pickAudioMime = () => {
    const cs = ['audio/mp4;codecs=mp4a.40.2', 'audio/webm;codecs=opus', 'audio/webm'];
    return cs.find(m => window.MediaRecorder.isTypeSupported(m)) || 'audio/webm';
  };

  const start = useCallback(async (mode = 'screen') => {
    setErrorMsg('');
    setState('starting');
    setActiveMode(mode);
    try {
      if (mode === 'audio') {
        if (!capabilities.audio) throw Object.assign(new Error('Audio recording not supported on this browser.'), { name: 'NotSupportedError' });
        const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        micStreamRef.current = mic;
        setMicActive(true); setTabAudioActive(false);
        const mime = pickAudioMime();
        const rec = new MediaRecorder(mic, { mimeType: mime, audioBitsPerSecond: 128_000 });
        wireRecorder(rec, mime, /*isAudio=*/true);
        rec.start(1000);
        return true;
      }

      if (mode === 'camera') {
        if (!capabilities.camera) throw Object.assign(new Error('Camera recording not supported on this browser.'), { name: 'NotSupportedError' });
        const cam = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width:  { ideal: quality === 'hd' ? 1920 : 1280 },
            height: { ideal: quality === 'hd' ? 1080 : 720 },
            frameRate: { ideal: quality === 'hd' ? 30 : 24 },
          },
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        cameraStreamRef.current = cam;
        setMicActive(cam.getAudioTracks().length > 0);
        setTabAudioActive(false);
        await beginCanvasPipeline(cam);
        return true;
      }

      // default: screen
      // Always attempt — detection is unreliable on some mobile browsers that
      // only expose getDisplayMedia under HTTPS/user-gesture. Let the real API
      // throw NotSupportedError and we surface a helpful message.
      const mdNow = navigator.mediaDevices;
      if (!mdNow || typeof mdNow.getDisplayMedia !== 'function') {
        throw Object.assign(
          new Error("Your browser can't capture the screen. On iPhone/Android, use the system screen recorder, or try Camera / Voice below."),
          { name: 'NotSupportedError' },
        );
      }
      const display = await mdNow.getDisplayMedia({
        video: {
          frameRate: quality === 'hd' ? 30 : 24,
          width: { ideal: quality === 'hd' ? 1920 : 1280 },
          displaySurface: 'browser',
        },
        audio: true,
        selfBrowserSurface: 'include',
        preferCurrentTab: true,
        surfaceSwitching: 'exclude',
        systemAudio: 'include',
      });
      displayStreamRef.current = display;
      setTabAudioActive(display.getAudioTracks().length > 0);
      let mic = null;
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        micStreamRef.current = mic; setMicActive(true);
      } catch { setMicActive(false); }
      await beginCanvasPipeline(display, mic);
      // detect if user hit browser "Stop sharing"
      const track = display.getVideoTracks()[0];
      if (track) track.onended = () => stop();
      return true;
    } catch (err) {
      cleanup();
      setState('error');
      const name = err?.name || '';
      let msg;
      if (name === 'NotAllowedError' || name === 'SecurityError')
        msg = 'Recording permission was denied. Please allow access and try again.';
      else if (name === 'NotFoundError')
        msg = 'No camera or microphone was found on this device.';
      else if (name === 'NotSupportedError' || name === 'TypeError')
        msg = err.message || 'This recording mode is not supported by your browser.';
      else if (name === 'NotReadableError')
        msg = 'Your camera or mic is already in use by another app.';
      else
        msg = err?.message || 'Could not start recording.';
      setErrorMsg(msg);
      onError?.(name || 'start_failed', msg);
      return false;
    }

    async function beginCanvasPipeline(videoStream, extraMicStream = null) {
      const video = document.createElement('video');
      video.srcObject = videoStream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      videoElRef.current = video;

      const settings = videoStream.getVideoTracks()[0]?.getSettings ? videoStream.getVideoTracks()[0].getSettings() : {};
      const w = settings.width || video.videoWidth || (quality === 'hd' ? 1280 : 960);
      const h = settings.height || video.videoHeight || (quality === 'hd' ? 720 : 540);

      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');

      const render = () => {
        try {
          if (video.readyState >= 2) ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          drawWatermark(ctx, canvas.width, canvas.height);
        } catch {}
        rafRef.current = requestAnimationFrame(render);
      };
      rafRef.current = requestAnimationFrame(render);

      const canvasStream = canvas.captureStream(quality === 'hd' ? 30 : 24);
      canvasStreamRef.current = canvasStream;

      const AC = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AC();
      const dest = audioCtx.createMediaStreamDestination();
      audioCtxRef.current = audioCtx; audioDestRef.current = dest;
      const attach = (s) => {
        if (!s || s.getAudioTracks().length === 0) return;
        try { audioCtx.createMediaStreamSource(s).connect(dest); } catch {}
      };
      attach(videoStream);
      attach(extraMicStream);

      const composed = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ]);
      const mime = pickVideoMime();
      const bps = quality === 'hd' ? 6_000_000 : 2_500_000;
      const rec = new MediaRecorder(composed, { mimeType: mime, videoBitsPerSecond: bps });
      wireRecorder(rec, mime, /*isAudio=*/false);
      rec.start(1000);
    }

    function wireRecorder(rec, mime, isAudio) {
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const totalMs = accumRef.current + (Date.now() - startedAtRef.current);
        const url = URL.createObjectURL(blob);
        const result = { blob, url, mime_type: mime, size: blob.size, duration_sec: Math.round(totalMs / 1000), kind: isAudio ? 'audio' : 'video' };
        cleanup();
        setState('idle'); setElapsed(0); setActiveMode(null);
        if (stopResolveRef.current) { stopResolveRef.current(result); stopResolveRef.current = null; }
      };
      rec.onerror = () => { setErrorMsg('Recording failed'); setState('error'); onError?.('recorder_error', 'Recording failed'); };
      recorderRef.current = rec;
      setPauseSupported(typeof rec.pause === 'function');
      startedAtRef.current = Date.now();
      accumRef.current = 0;
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((accumRef.current + (Date.now() - startedAtRef.current)) / 1000));
      }, 250);
      maxTimerRef.current = setTimeout(() => { stop(); }, maxDurationSec * 1000);
      setState('recording');
    }
  }, [capabilities.audio, capabilities.camera, capabilities.screen, cleanup, drawWatermark, maxDurationSec, onError, quality]);

  const pause = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== 'recording') return;
    try {
      rec.pause();
      accumRef.current += Date.now() - startedAtRef.current;
      clearInterval(timerRef.current);
      setState('paused');
    } catch {}
  }, []);

  const resume = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== 'paused') return;
    try {
      rec.resume();
      startedAtRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((accumRef.current + (Date.now() - startedAtRef.current)) / 1000));
      }, 250);
      setState('recording');
    } catch {}
  }, []);

  const stop = useCallback(() => new Promise((resolve) => {
    const rec = recorderRef.current;
    if (!rec || rec.state === 'inactive') { cleanup(); resolve(null); return; }
    setState('processing');
    stopResolveRef.current = resolve;
    try { rec.stop(); } catch { resolve(null); }
  }), [cleanup]);

  return {
    state, elapsed, errorMsg, pauseSupported, micActive, tabAudioActive,
    capabilities, anySupported, activeMode,
    // kept for backwards compat
    supported: capabilities.screen,
    start, pause, resume, stop,
  };
}
